<template>
    <div id="today-view"
         class="full-vue-page">
        <TodayViewFilterBar :initial-date="date"
                            :selected-layout="selectedLayoutName"
                            @date-change="setDate"
                            @filters-change="setFiltersAndUpdateScreen"
                            @layout-change="setLayout" />

        <component :is="selectedLayoutComponent"
                   :ref="selectedLayoutName"
                   :sessions="sessions"
                   :events="events"
                   :session-hours="sessionHours"
                   :session-availabilities="sessionAvailabilities"
                   :session-count="sessionCount"
                   :session-count-before-filters="sessionCountBeforeFilters"
                   :is-loading="isLoading"
                   :show-hours-list="showHoursList"
                   :current-hour="currentHour"
                   @layout-change="setLayout"
                   @right-scroll="onRightScroll"
                   @select-hour="jumpToHour" />
    </div>
</template>

<script>
import _ from 'lodash';
import EntityStatus from '@/lib/Entities/EntityStatus';
import EventNames from '@/lib/Events/EventNames';
import LocalStorageKeys from '@/lib/Storage/LocalStorageKeys';
import moment from 'moment';
import TodayViewFilterBar from './components/TodayViewFilterBar';
import TodayViewLinearLayout from './TodayViewLinearLayout';
import TodayViewListLayout from './TodayViewListLayout';
import { cloneShallow } from '@/functions/clone';
import { container } from '@/container';
import {
    filterCategoriesByVisibleItems,
    filterCategoryIDs,
    filterEventIDs,
    filterEventIDsByCategory,
    filterEventsByVisibleItems,
    filterSessionsForTodayViewAfterAvailability,
    filterSessionsForTodayViewBeforeAvailability,
    filterSoldOutSessions,
} from './todayViewFilters';
import { isElementVisible } from '@/functions/dom';
import { mapState } from 'vuex';
import { organiseTicketsByEvent } from '@/functions/tickets';
import { TodayViewLayoutName } from '@/epos/vue/pages/today/TodayViewLayoutName';
import { toYMD } from '@/functions/date';

/**
 * @typedef {import('@/lib/DeviceGroups/DeviceGroup')} DeviceGroup
 */

export default {
    name: 'TodayView',

    components: {
        TodayViewFilterBar,
        TodayViewListLayout,
        TodayViewLinearLayout,
    },

    data() {
        return {
            firstVisibleSession: null,

            loadingEvents: false,
            loadingSessions: false,

            // Replaced by an actual function in 'created'.
            loadSessionsDebounced: null,
            onRightScrollDebounced: null,

            /**
             * Name of the selected layout.
             *
             * @type {"list"|"linear"}
             */
            selectedLayoutName: undefined,

            /**
             * Name of the Vue component to use for the selected layout.
             *
             * @type {string}
             */
            selectedLayoutComponent: null,
            allEventIDs: [],
            activeEventIDs: [],
            allCategoryIDs: [],
            activeCategoryIDs: [],
        };
    },

    computed: {
        ...mapState('today', [
            'date',
            'filters',
            'hideInactiveCategoriesAndEvents',
            'hideSoldOut',
            'sessions',
            'sessionCount',
            'sessionCountBeforeFilters',
            'events',
            'sessionHours',
            'sessionAvailabilities',
            'ticketsByEvent',
        ]),

        ...mapState(['currentItemBranch']),

        ...mapState('cart', ['currentCartThirdPartyRef']),

        isLoading() {
            return this.loadingEvents
                || this.loadingSessions;
        },

        /**
         * The "current hour" is the hour that is highlighted on the left of the page.
         * The highlighted hour is the hour in which the first visible session (on the right) begins.
         *
         * @return {Number|null}
         */
        currentHour() {
            return this.firstVisibleSession ? this.firstVisibleSession.startTime.getHours() : null;
        },

        isScrolledToFirstSession() {
            return this.sessions !== null
                && this.sessions[0]
                && this.firstVisibleSession
                && this.firstVisibleSession.ID === this.sessions[0].ID;
        },

        isScrolledToLastSession() {
            // This only works on desktop.
            return this.sessions !== null
                && this.sessions[this.sessions.length - 1]
                && this.firstVisibleSession
                && this.firstVisibleSession.ID === this.sessions[this.sessions.length - 1].ID;
        },

        showHoursList() {
            return Object.keys(this.sessionHours).length > 0;
        },

        /**
         * @returns {DeviceGroup}
         */
        deviceGroup() {
            return container.currentDevice.getDevice()?.deviceGroup ?? null;
        },
    },

    watch: {
        /**
         * When the current hour (determined from the first visible session) changes,
         * ensure the current hour on the left is scrolled into view.
         */
        currentHour() {
            this.$nextTick(() => {
                // Ensure the current hour is visible in the left list.
                if (this.currentHour === null) {
                    return;
                }

                // See https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
                // We only call scrollIntoView if it's not already visible because it's annoying when the list
                // scrolls itself just by clicking on a new hour.
                const el = document.getElementById(`today-view-hour-list--${this.currentHour}`);
                if (!el) {
                    return;
                }

                const containerEl = document.getElementById('today-view-hour-list');
                if (!isElementVisible(el, containerEl)) {
                    el.scrollIntoView(false);
                }
            });
        },

        currentCartThirdPartyRef() {
            // This is triggered when the cart changes.
            // We are doing this to make sure the state of the 'x In cart' label is always updated.
            // Because when we add tickets we go via the Sell Screen, this is only actually triggered very rarely for
            // example when we clear the cart from the preview modal. Therefore, for now this simplistic update should
            // do everything we need. Going forward we may need to be more nuanced and do things like compare specific
            // parts of the newCart with the oldCart.
            this.loadSessions();
        },
    },

    created() {
        // Because the itemBranch can also be set on the Sell screen ensure that we set it the value in filters early
        // so that if itemBranch !== sellBranch we can show that a filter is applied with the dot.
        this.$store.state.today.filters.itemBranchID = this.currentItemBranch.ID;

        this.loadSessionsDebounced = _.debounce(
            () => {
                this.loadSessions();
            },
            300
        );

        this.onRightScrollDebounced = _.debounce(
            () => {
                this.determineFirstVisibleSession();
            },
            10,
            {
                leading: false,
                trailing: true,
            }
        );

        this.setLayout(
            container.localStorageDataStore.find(LocalStorageKeys.TODAY_VIEW_LAYOUT) || TodayViewLayoutName.LIST
        );

        // Load step by step as we do need tickets when loading events, tickets and events when loading categories
        // for the proper filtering by visibility.
        this.loadTickets()
            .then(() => this.loadEvents())
            .then(() => this.loadCategories())
            .then(() => this.loadSessions());

        // Listen for changes to the item branch.
        // We do this by listening for the "items synced" event, instead of watching for changes
        // to the selected item branch, because we need to wait for sessions to be downloaded for the
        // newly selected branch.
        // The items synced event is always broadcast called when the item branch changes,
        // even if nothing new was synced.
        //
        // We need to remove the listener when navigating away from the today screen. $on returns
        // a function to do this, we'll store that and call it in beforeUnmount.
        this.deregisterItemsSyncedListenerFunc = container.$rootScope.$on(EventNames.ITEMS_SYNCED, () => {
            this.loadSessions();
            this.loadTickets();
        });
    },

    beforeUnmount() {
        if (this.deregisterItemsSyncedListenerFunc) {
            this.deregisterItemsSyncedListenerFunc();
        }
    },

    methods: {
        loadTickets() {
            /**
             * @type {TicketRepository}
             */
            const ticketRepository = container.ticketRepository;
            // The today screen now uses the item branch.
            return ticketRepository.findActiveForBranch(this.currentItemBranch.ID)
                .then((tickets) => {
                    const ticketsByEvent = organiseTicketsByEvent(tickets);
                    this.$store.commit('today/setTicketsByEvent', ticketsByEvent);
                });
        },

        setDate(newDate) {
            this.$store.commit('today/setDate', newDate);

            // Show the loader now for a fast UI update.
            this.setLoadingSessions(true);

            // Clear the existing list of sessions so we're not showing sessions for the wrong date.
            this.$store.commit('today/clearSessions');

            // Call a debounced loadSessions to prevent loading sessions for lots of days if you click
            // the next/previous date button rapidly.
            this.loadSessionsDebounced();
        },

        setFiltersAndUpdateScreen(newFilters) {
            // TODO: ensure that the provided filters are valid.
            this.$store.commit('today/setFilters', newFilters);

            this.changeBranch(newFilters.itemBranchID);

            // Show the loader now for a fast UI update.
            this.setLoadingSessions(true);

            // Reload the sessions after changing filters.
            this.loadSessionsDebounced();
        },

        /**
         * @param {string} layout
         */
        setLayout(layout) {
            // Persist the layout choice to local storage. (Even if it's the same - that doesn't matter).
            container.localStorageDataStore.persist(LocalStorageKeys.TODAY_VIEW_LAYOUT, layout);

            this.selectedLayoutName = layout;

            switch (layout) {
                case TodayViewLayoutName.LINEAR:
                    this.selectedLayoutComponent = 'TodayViewLinearLayout';
                    break;
                case TodayViewLayoutName.LIST:
                    this.selectedLayoutComponent = 'TodayViewListLayout';
                    break;
                default:
                    return;
            }

            // TODO: we may need to refactor how we call this method as we also call it when sessions are loaded.
            this.onSessionsVisible();
        },

        /**
         * @param {number} newBranchID
         */
        changeBranch(newBranchID) {
            const oldBranch = container.userService.currentItemBranch;

            if (newBranchID === oldBranch.ID) {
                return;
            }

            let newBranch = container.userService.getBranchByID(newBranchID);

            console.log(
                'Changing item branch',
                {
                    oldBranchID: oldBranch ? oldBranch.ID : null,
                    newBranchID: newBranch ? newBranch.ID : null,
                },
            );

            container.userService.changeItemBranch(newBranch);
        },

        loadSessions() {
            this.setLoadingSessions(true);
            container.sessionRepository
                .getSessionsForDate(
                    this.date,
                    // The today screen now uses the item branch.
                    this.currentItemBranch.ID,
                )
                .then(async (sessions) => {
                    this.$store.commit('today/setSessionCountBeforeFilters', sessions.length);

                    // We don't want to get stuck in a loop here.
                    let filters = cloneShallow(this.filters);

                    // Filter and normalize the events and categories then we can use this array to filter the sessions.
                    filters = filterEventIDs(filters, this.allEventIDs, this.activeEventIDs);
                    filters = filterCategoryIDs(filters, this.allCategoryIDs, this.activeCategoryIDs);

                    // Filter events further, so we only have events that are in the filtered categories.
                    filters = filterEventIDsByCategory(filters, this.events);

                    // Store eventIDs filtered by Events and Categories.
                    this.$store.commit('today/setFilteredEventIDs', filters.eventIDs);

                    // We now have a filtered array of events that are valid for the selected categories.

                    // Filter the sessions an initial time.
                    // This will remove sessions with the wrong event or status.
                    // We do this first so that we don't need to request availability for irrelevant sessions.
                    // The filters we do are in order:
                    // 1. Filter by eventID
                    // 2. Filter by status
                    sessions = filterSessionsForTodayViewBeforeAvailability(
                        sessions,
                        filters
                    );

                    // Load the new availability.
                    return container.sessionAvailabilityService
                        .loadSessionAvailabilities(
                            sessions
                            // This doesn't send tickets or the originalOrderID because it's not applicable here
                            // on the Today screen.
                        )
                        .then((availabilities) => {
                            // Run the remaining filters that depend on availability.
                            sessions = filterSessionsForTodayViewAfterAvailability(
                                sessions,
                                availabilities,
                                filters
                            );

                            if (filters.hideSoldOut) {
                                sessions = filterSoldOutSessions(
                                    sessions,
                                    availabilities
                                );
                            }

                            // Update the availabilities to show the qty (if any) that is already in the cart.
                            // Note: this will also update the availableQty.
                            const cartSpaceCalculator = container.sessionSpaceCalculatorFactory.create();
                            sessions.forEach((session) => {
                                cartSpaceCalculator.applySpacesInCartToAvailability(availabilities[session.ID]);
                            });
                            this.setSessions(sessions, availabilities);
                        })
                        .catch((e) => {
                            console.warn('Today view failed to load session availability (maybe the device is offline)', e);
                            this.setSessions(sessions, {});
                        });
                })
                .finally(() => {
                    this.$nextTick(() => {
                        this.setLoadingSessions(false);
                    });
                });
        },

        setLoadingSessions(value) {
            this.loadingSessions = value;

            if (!value && this.sessions !== null && this.sessions.length > 0) {
                this.onSessionsVisible();
            }
        },

        /**
         * @param {Session[]} sessions
         * @param {Object.<number, SessionAvailability>} sessionAvailabilities
         */
        setSessions(sessions, sessionAvailabilities) {
            // Sort the sessions by start time.
            // This is the sorting used by the List View.
            sessions.sort((a, b) => (a.startTime > b.startTime ? 1 : -1));

            // Extract the hours that have sessions in them.
            // This produces an object with keys as hours (0 - 23) and values are a formatted string to display that
            // hour, and a number of sessions on that hour.
            let sessionHours = {};
            sessions.forEach((s) => {
                const hour = s.startTime.getHours();
                if (!sessionHours.hasOwnProperty(hour)) {
                    sessionHours[hour] = {
                        displayHour: moment(s.startTime).format('hA'),
                        sessionCount: 0,
                    };
                }

                sessionHours[hour].sessionCount += 1;
            });

            this.$store.commit('today/setSessions', {
                sessions,
                sessionAvailabilities,
                sessionHours,
            });
        },

        onSessionsVisible() {
            this.$nextTick(() => {
                // If this is today, scroll to the next session in the future instead
                // of starting at the top of today.
                const now = new Date();
                const nextSession = this.findFirstSessionAfterDate(now);
                if (nextSession) {
                    this.jumpToSession(nextSession);
                }

                this.determineFirstVisibleSession();

                if (this.$refs[TodayViewLayoutName.LINEAR]) {
                    this.$refs[TodayViewLayoutName.LINEAR].scrollToHour();
                }
            });
        },

        loadCategories() {
            return container.categoryRepository.findAll()
                .then((categories) => {
                    const visibleCategories = filterCategoriesByVisibleItems(
                        categories,
                        Object.values(this.events ?? {}),
                        this.ticketsByEvent,
                        this.deviceGroup,
                    );

                    const categoriesObject = {};
                    const allCategoryIDs = [];
                    const activeCategoryIDs = [];
                    // Get needed data with just one cycle.
                    visibleCategories.forEach((category) => {
                        const { ID: catID } = category;
                        categoriesObject[catID] = category;
                        allCategoryIDs.push(catID);
                        if (category.status === EntityStatus.ACTIVE) {
                            activeCategoryIDs.push(catID);
                        }
                    });
                    this.$store.commit('today/setCategories', categoriesObject);
                    this.allCategoryIDs = allCategoryIDs;
                    this.activeCategoryIDs = activeCategoryIDs;
                });
        },

        loadEvents() {
            this.loadingEvents = true;
            return container.sessionEventRepository.findAll()
                .then((events) => {
                    const visibleEvents = filterEventsByVisibleItems(
                        events,
                        this.ticketsByEvent,
                        this.deviceGroup,
                    );

                    const eventsObject = {};
                    const allEventIDs = [];
                    const activeEventIDs = [];
                    // Get needed data with just one cycle.
                    visibleEvents.forEach((event) => {
                        const { ID: eventID } = event;
                        allEventIDs.push(eventID);
                        if (event.status === EntityStatus.ACTIVE) {
                            activeEventIDs.push(eventID);
                            eventsObject[eventID] = event;
                        } else if (event.status === EntityStatus.INACTIVE) {
                            eventsObject[eventID] = event;
                        }
                    });
                    this.$store.commit('today/setEvents', eventsObject);
                    this.allEventIDs = allEventIDs;
                    this.activeEventIDs = activeEventIDs;
                })
                .finally(() => {
                    this.loadingEvents = false;
                });
        },

        jumpToHour(hour) {
            // Convert the integer hour to a Date object.
            const d = (new Date(toYMD(this.date)));
            d.setHours(hour);
            d.setMinutes(0);
            d.setSeconds(0);
            d.setMilliseconds(0);

            this.jumpToFirstSessionAfterDate(d);
        },

        /**
         * @param {Date} date
         */
        jumpToFirstSessionAfterDate(date) {
            const session = this.findFirstSessionAfterDate(date);

            if (session) {
                this.jumpToSession(session);
            }
        },

        jumpToFirstSession() {
            const session = this.sessions[0];

            if (session) {
                this.jumpToSession(session);
            }
        },

        jumpToLastSession() {
            const session = this.sessions[this.sessions.length - 1];

            if (session) {
                this.jumpToSession(session);
            }
        },

        /**
         * Returns the first session that starts at or after the supplied hour.
         *
         * @param {Date} date
         *
         * @return {?Session}
         */
        findFirstSessionAfterDate(date) {
            if (!this.sessions) {
                return null;
            }
            return this.sessions.find((s) => s.startTime >= date);
        },

        jumpToSession(session) {
            if (this.$refs[TodayViewLayoutName.LIST]) {
                this.$refs[TodayViewLayoutName.LIST].jumpToSession(session);
            }
        },

        onRightScroll() {
            this.onRightScrollDebounced();
        },

        /**
         * @return {Session|null}
         */
        determineFirstVisibleSession() {
            if (!(this.sessions instanceof Array)) {
                // No sessions? Nothing can be visible!
                this.firstVisibleSession = null;
                return null;
            }

            if (this.$refs[this.selectedLayoutName]?.determineFirstVisibleSession) {
                const firstSession = this.$refs[this.selectedLayoutName].determineFirstVisibleSession();
                this.firstVisibleSession = firstSession;
                return firstSession;
            }

            return null;
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables";

#today-view {
    background: white;
    display: flex;
    flex-direction: column;

    &__filter-bars {
        flex-grow: 0;
        flex-shrink: 0;
    }
}
</style>
